import { Wind, Droplets, Gauge, Eye, Sunrise, Sunset, Navigation2 } from "lucide-react";
import {
  speed,
  speedUnit,
  visibility,
  uvInfo,
  windDirection,
  formatClock,
} from "../lib/weather";

const Card = ({ label, icon: Icon, testid, children }) => (
  <div data-testid={testid} className="glass-card p-4 sm:p-5">
    <div className="flex items-center gap-2 text-muted-foreground mb-3">
      <Icon className="w-4 h-4" />
      <span className="text-[11px] font-semibold uppercase tracking-widest">{label}</span>
    </div>
    {children}
  </div>
);

export const MetricsGrid = ({ current, units }) => {
  const uv = uvInfo(current.uvi);
  const off = current.__offset ?? 0;

  // sun arc progress
  const now = current.dt;
  const dayLen = current.sunset - current.sunrise;
  const prog = Math.max(0, Math.min(1, (now - current.sunrise) / (dayLen || 1)));
  const arcX = 10 + prog * 80;
  const arcY = 45 - Math.sin(prog * Math.PI) * 35;

  return (
    <div className="grid grid-cols-2 gap-4" data-testid="metrics-grid">
      <Card label="Angin" icon={Wind} testid="metric-wind">
        <div className="flex items-center gap-3">
          <div className="relative w-12 h-12 rounded-full border-2 border-border grid place-items-center">
            <Navigation2
              className="w-5 h-5 text-primary"
              style={{ transform: `rotate(${current.wind_deg}deg)` }}
              fill="currentColor"
            />
          </div>
          <div>
            <div className="text-2xl font-bold num">{speed(current.wind_speed, units)}</div>
            <div className="text-xs text-muted-foreground">
              {speedUnit(units)} · {windDirection(current.wind_deg)}
            </div>
          </div>
        </div>
      </Card>

      <Card label="Kelembapan" icon={Droplets} testid="metric-humidity">
        <div className="text-3xl font-bold num">{current.humidity}%</div>
        <div className="mt-2 h-1.5 rounded-full bg-secondary overflow-hidden">
          <div className="h-full bg-sky-500" style={{ width: `${current.humidity}%` }} />
        </div>
      </Card>

      <Card label="Indeks UV" icon={Gauge} testid="metric-uv">
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-bold num">{current.uvi}</span>
          <span className="text-sm font-semibold" style={{ color: uv.color }}>
            {uv.label}
          </span>
        </div>
        <div className="mt-2 h-1.5 rounded-full bg-gradient-to-r from-green-500 via-yellow-500 to-purple-600 relative">
          <div
            className="absolute -top-0.5 w-2.5 h-2.5 rounded-full bg-white border-2 border-foreground"
            style={{ left: `${Math.min(100, (current.uvi / 12) * 100)}%` }}
          />
        </div>
      </Card>

      <Card label="Tekanan" icon={Gauge} testid="metric-pressure">
        <div className="text-3xl font-bold num">{current.pressure}</div>
        <div className="text-xs text-muted-foreground">hPa</div>
      </Card>

      <Card label="Jarak Pandang" icon={Eye} testid="metric-visibility">
        <div className="text-3xl font-bold num">{visibility(current.visibility, units)}</div>
      </Card>

      <Card label="Matahari" icon={Sunrise} testid="metric-sun">
        <svg viewBox="0 0 100 50" className="w-full h-12">
          <path d="M10 45 Q50 -8 90 45" fill="none" stroke="hsl(var(--border))" strokeWidth="2" strokeDasharray="3 3" />
          <circle cx={arcX} cy={arcY} r="4" fill="#F59E0B" />
        </svg>
        <div className="flex justify-between text-xs num mt-1">
          <span className="flex items-center gap-1 text-amber-500">
            <Sunrise className="w-3.5 h-3.5" />
            {formatClock(current.sunrise, off)}
          </span>
          <span className="flex items-center gap-1 text-orange-500">
            <Sunset className="w-3.5 h-3.5" />
            {formatClock(current.sunset, off)}
          </span>
        </div>
      </Card>
    </div>
  );
};
